import {
    CompressionType,
    INodeResponse,
    INodeSettingsResponse,
} from '@kcftech/prometheus-client';
import { DisabledOptionInfo } from 'components/dropdown/Dropdown';
import { useMemo } from 'react';
import {
    allowedNumberOfSamplesForHighPeriodicity,
    collectionIntervalExceedsUpdateInterval,
    getEffectiveUpdateInterval,
    getNodeSettingValue,
    isAcquisitionUnit,
    isAnalogNode,
    isIepeNode,
    isNodeSettingEqualToValue,
    isPressureNodeType,
    isTriggeredPiezoUnit,
    isV3PremiumVibrationNode,
    isV4VibrationNode,
    isVoltageOrCurrentInputNodeType,
    OptionChild,
} from 'utils/node';

export type UseNumberOfSamplesInfo = Readonly<{
    node: INodeResponse | undefined;
    port?: number;
    nodeSettings: INodeSettingsResponse | undefined;
}>;

// TODO: Replace these options with those that come from the API
export const analogOptions: OptionChild<number>[] = [
    {
        Text: '32 Samples',
        Value: 32,
    },
    {
        Text: '64 Samples',
        Value: 64,
    },
    {
        Text: '128 Samples',
        Value: 128,
    },
    {
        Text: '256 Samples',
        Value: 256,
    },
    {
        Text: '512 Samples',
        Value: 512,
    },
    {
        Text: '1024 Samples',
        Value: 1024,
    },
    {
        Text: '2048 Samples',
        Value: 2048,
    },
    {
        Text: '4096 Samples',
        Value: 4096,
    },
];

export const iepeOptions: OptionChild<number>[] = [
    {
        Text: '32 Samples',
        Value: 32,
    },
    {
        Text: '64 Samples',
        Value: 64,
    },
    {
        Text: '128 Samples',
        Value: 128,
    },
    {
        Text: '256 Samples',
        Value: 256,
    },
    {
        Text: '512 Samples',
        Value: 512,
    },
    {
        Text: '1024 Samples',
        Value: 1024,
    },
    {
        Text: '2048 Samples',
        Value: 2048,
    },
    {
        Text: '4096 Samples',
        Value: 4096,
    },
];

export const pressureOptions: OptionChild<number>[] = [
    {
        Text: '32 Samples',
        Value: 32,
    },
    {
        Text: '1024 Samples',
        Value: 1024,
    },
    {
        Text: '1650 Samples',
        Value: 1650,
    },
];

export const voltageAndCurrentInputNodeOptions: OptionChild<number>[] = [
    {
        Text: '32 Samples',
        Value: 32,
    },
    {
        Text: '1024 Samples',
        Value: 1024,
    },
];

export const v3PremiumVibrationOptions: OptionChild<number>[] = [
    {
        Text: '1650 Samples',
        Value: 1650,
    },
    {
        Text: '4096 Samples',
        Value: 4096,
    },
];

export const v4VibrationOptions: OptionChild<number>[] = [
    {
        Text: '6000 Samples',
        Value: 6000,
    },
    {
        Text: '8000 Samples',
        Value: 8000,
    },
];

export const envelopingOptions: OptionChild<number>[] = [
    {
        Text: '8000 Samples',
        Value: 8000,
    },
    {
        Text: '12000 Samples',
        Value: 12000,
    },
];

export function useNumberOfSamplesOptions({
    node,
    port,
    nodeSettings,
}: UseNumberOfSamplesInfo) {
    const isV3PremiumVibration = useMemo(
        () => (node ? isV3PremiumVibrationNode(node) : false),
        [node],
    );
    const isV4Vibration = useMemo(() => (node ? isV4VibrationNode(node) : false), [node]);
    const isPressure = useMemo(
        () => isPressureNodeType(node?.NodeTypeCode),
        [node?.NodeTypeCode],
    );
    const isVoltageOrCurrentInput = useMemo(
        () => isVoltageOrCurrentInputNodeType(node?.NodeTypeCode),
        [node?.NodeTypeCode],
    );
    const isAnalog = useMemo(() => (node ? isAnalogNode(node) : false), [node]);
    const isIepe = useMemo(() => (node ? isIepeNode(node) : false), [node]);
    const isAcqUnit = useMemo(() => (node ? isAcquisitionUnit(node) : false), [node]);

    const canAssignSamples =
        isV3PremiumVibration ||
        isV4Vibration ||
        isPressure ||
        isVoltageOrCurrentInput ||
        isAnalog ||
        isIepe ||
        isAcqUnit;

    const filteredVibrationOptions = useMemo(
        () =>
            isNodeSettingEqualToValue(
                nodeSettings?.CompressionType,
                CompressionType.Carl2,
            )
                ? v3PremiumVibrationOptions.filter(
                      (c) => c.Value === allowedNumberOfSamplesForHighPeriodicity,
                  )
                : v3PremiumVibrationOptions,
        [nodeSettings?.CompressionType],
    );

    const validSampleOptions = useMemo(() => {
        if (isV3PremiumVibration) {
            return filteredVibrationOptions;
        }
        if (isV4Vibration) {
            return v4VibrationOptions;
        }
        if (isPressure) {
            return pressureOptions;
        }
        if (isVoltageOrCurrentInput) {
            return voltageAndCurrentInputNodeOptions;
        }
        if (isIepe) {
            return iepeOptions;
        }
        if (isAnalog) {
            return analogOptions;
        }
        if (isAcqUnit) {
            return envelopingOptions;
        }
        return undefined;
    }, [
        filteredVibrationOptions,
        isAcqUnit,
        isAnalog,
        isIepe,
        isPressure,
        isVoltageOrCurrentInput,
        isV3PremiumVibration,
        isV4Vibration,
    ]);

    const disabledOptions = useMemo(() => {
        if (!validSampleOptions) {
            return [];
        }

        const piezoCollectionType = getNodeSettingValue(
            nodeSettings?.PiezoCollectionType,
        );
        const updateInterval = getEffectiveUpdateInterval(
            node?.NodeTypeCode,
            piezoCollectionType,
            getNodeSettingValue(nodeSettings?.UpdateInterval),
        );
        const disabledMessage = isTriggeredPiezoUnit(
            node?.NodeTypeCode,
            piezoCollectionType,
        )
            ? 'Collection duration must fit within the 10-minute trigger window for triggered Piezo Units. Please adjust to save.'
            : 'Collection duration is greater than update interval. Please adjust to save.';

        const result: DisabledOptionInfo[] = [];

        for (const option of validSampleOptions) {
            if (port !== undefined) {
                const portSettings =
                    nodeSettings?.ConnectedIepeTransducerSettings?.[port];

                if (
                    collectionIntervalExceedsUpdateInterval({
                        samplingFrequency: getNodeSettingValue(
                            portSettings?.SamplingFrequency,
                        ),
                        numberOfSamples: option.Value,
                        updateInterval,
                    }) ||
                    collectionIntervalExceedsUpdateInterval({
                        samplingFrequency: getNodeSettingValue(
                            portSettings?.EnvelopingSettings?.SamplingFrequency,
                        ),
                        numberOfSamples: option.Value,
                        updateInterval,
                    })
                ) {
                    result.push({ key: option.Text, message: disabledMessage });
                    continue;
                }
            }

            if (
                collectionIntervalExceedsUpdateInterval({
                    samplingFrequency: getNodeSettingValue(
                        nodeSettings?.SamplingFrequency,
                    ),
                    numberOfSamples: option.Value,
                    updateInterval,
                })
            ) {
                result.push({ key: option.Text, message: disabledMessage });
            }
        }

        return result;
    }, [
        node?.NodeTypeCode,
        nodeSettings?.ConnectedIepeTransducerSettings,
        nodeSettings?.PiezoCollectionType,
        nodeSettings?.SamplingFrequency,
        nodeSettings?.UpdateInterval,
        port,
        validSampleOptions,
    ]);

    return { canAssignSamples, validSampleOptions, disabledOptions };
}
